import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Search, Truck, CheckCircle, Clock, AlertCircle, ExternalLink, MapPin, Mail, Hash } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackOrder = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!orderNumber.trim() || !email.trim()) {
      toast.error('Please enter both order number and email');
      return;
    }
    
    setLoading(true);
    setError('');
    setOrder(null);
    
    try {
      const response = await axios.post(`${API}/track-order`, {
        order_number: orderNumber.trim(),
        email: email.trim().toLowerCase()
      });
      setOrder(response.data);
    } catch (err) {
      const message = err.response?.data?.detail || 'Order not found. Please check your details.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'shipped':
        return <Truck className="w-6 h-6 text-blue-600" />;
      case 'processing':
      case 'confirmed':
        return <Clock className="w-6 h-6 text-yellow-600" />;
      case 'cancelled':
        return <AlertCircle className="w-6 h-6 text-red-600" />;
      default:
        return <Package className="w-6 h-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'shipped':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'processing':
      case 'confirmed':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusMessage = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'Your order has been delivered!';
      case 'shipped':
        return 'Your order is on its way!';
      case 'processing':
        return 'Your order is being prepared.';
      case 'confirmed':
        return 'Your order has been confirmed and will be processed soon.';
      case 'pending':
        return 'Your order is pending confirmation.';
      case 'cancelled':
        return 'This order has been cancelled.';
      default:
        return 'Order status unknown.';
    }
  };

  const getTrackingUrl = (carrier, trackingNumber) => {
    if (!carrier || !trackingNumber) return null;
    const c = carrier.toLowerCase();
    if (c.includes('usps')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    if (c.includes('ups')) return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    if (c.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    if (c.includes('dhl')) return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackingNumber}`;
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const statusSteps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
  const currentStepIndex = order ? statusSteps.indexOf(order.status?.toLowerCase()) : -1;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F9F9] to-white" data-testid="track-order-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#D4AF37]/10 rounded-full mb-6">
            <Package className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h1
            className="text-3xl md:text-4xl font-bold mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="track-order-title"
          >
            Track Your Order
          </h1>
          <p className="text-gray-600 max-w-md mx-auto">
            Enter your order number and email address to check the status of your shipment.
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8" data-testid="track-order-form">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Hash className="w-4 h-4 text-gray-400" />
                  Order Number
                </label>
                <input
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                  placeholder="e.g., NP2024XXXXXX"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-lg font-mono bg-gray-50"
                  data-testid="order-number-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="The email used for your order"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent bg-gray-50"
                  data-testid="email-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1A1A1A] text-white py-4 rounded-xl hover:bg-black transition-colors font-medium flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
              data-testid="track-button"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Track Order
                </>
              )}
            </button>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-8 flex items-center gap-3" data-testid="error-message">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Order Results */}
        {order && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="order-results">
            {/* Order Header */}
            <div className="bg-gradient-to-r from-[#1A1A1A] to-[#333] text-white p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Order Number</p>
                  <p className="text-2xl font-mono font-bold">{order.order_number}</p>
                  <p className="text-sm text-gray-400 mt-2">Placed on {formatDate(order.created_at)}</p>
                </div>
                <div className={`self-start md:self-center px-5 py-2.5 rounded-full border-2 ${getStatusColor(order.status)} flex items-center gap-2`}>
                  {getStatusIcon(order.status)}
                  <span className="font-semibold capitalize">{order.status}</span>
                </div>
              </div>
              <p className="mt-4 text-gray-300 text-sm">{getStatusMessage(order.status)}</p>
            </div>

            {/* Progress Tracker */}
            {order.status !== 'cancelled' && (
              <div className="p-6 md:p-8 border-b bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-6">Order Progress</h3>
                <div className="flex items-center justify-between relative">
                  {/* Progress Line */}
                  <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 z-0 rounded-full">
                    <div
                      className="h-full bg-gradient-to-r from-[#D4AF37] to-[#B5952F] transition-all duration-500 rounded-full"
                      style={{ width: `${Math.max(0, (currentStepIndex / (statusSteps.length - 1)) * 100)}%` }}
                    />
                  </div>
                  
                  {statusSteps.map((step, index) => (
                    <div key={step} className="flex flex-col items-center relative z-10">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-sm ${
                          index <= currentStepIndex
                            ? 'bg-[#D4AF37] text-white shadow-[#D4AF37]/30'
                            : 'bg-white border-2 border-gray-200 text-gray-400'
                        }`}
                      >
                        {index < currentStepIndex ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <span className="text-sm font-bold">{index + 1}</span>
                        )}
                      </div>
                      <span className={`text-xs mt-2 capitalize font-medium ${index <= currentStepIndex ? 'text-[#D4AF37]' : 'text-gray-400'}`}>
                        {step}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tracking Info */}
            {order.tracking_number && (
              <div className="p-6 md:p-8 border-b bg-blue-50">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Truck className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-blue-900">Shipment Tracking</h3>
                </div>
                <div className="bg-white rounded-xl p-5 border border-blue-200 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Tracking Number</p>
                      <p className="font-mono font-bold text-xl text-gray-900">{order.tracking_number}</p>
                      {order.carrier && <p className="text-sm text-blue-600 mt-1 font-medium">{order.carrier}</p>}
                    </div>
                    {getTrackingUrl(order.carrier, order.tracking_number) && (
                      <a
                        href={getTrackingUrl(order.carrier, order.tracking_number)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-600 text-white px-5 py-3 rounded-xl flex items-center gap-2 hover:bg-blue-700 transition-colors font-medium shadow-sm"
                        data-testid="track-package-link"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Track Package
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Order Details */}
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Items */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2 text-gray-900">
                    <Package className="w-5 h-5 text-[#D4AF37]" />
                    Order Items
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-2 border-b border-gray-100 last:border-0">
                        <span className="text-gray-700">{item.product_name}</span>
                        <span className="text-gray-500 font-medium">x{item.quantity}</span>
                      </div>
                    ))}
                    <div className="pt-3 flex justify-between font-semibold text-lg">
                      <span>Total</span>
                      <span className="text-[#D4AF37]">${order.total?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Shipping */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2 text-gray-900">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                    Shipping To
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-5 text-sm">
                    <p className="text-gray-700 font-medium">{order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.zip_code}</p>
                    <p className="text-gray-500 mt-3 capitalize flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      {order.shipping_method === 'pickup' ? 'Local Pickup' : 'Standard Delivery'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Last Update */}
              {order.updated_at && order.updated_at !== order.created_at && (
                <div className="mt-6 pt-6 border-t text-sm text-gray-500 text-center">
                  Last updated: {formatDate(order.updated_at)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-10 text-center bg-white rounded-xl p-6 shadow-sm">
          <p className="text-gray-600 mb-3">
            Can't find your order or need help?
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#B5952F] font-medium transition-colors"
          >
            <Mail className="w-4 h-4" />
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TrackOrder;
